import React, { MouseEvent, useMemo, useState } from 'react';
import { SECURITY_ACTIONS } from '../../../common/auth/Security';
import type { SecuredActionType } from '../../../common/components/ActionType';
import Confirm from '../../../common/components/Confirm';
import ResponsiveActions from '../../../common/components/ResponsiveActions';
import { ENTITY_CONSTANTS } from '../../../common/constants/entityConstants';
import I18n from '../../../common/i18n/I18n';
import { Icons } from '../../../common/icons/Icons';
import { useDeleteNote } from '../../../common/notes';
import { ProductionStatusNoteHeaderProps } from './ProductionStatusNoteHeader';

export default function ProductionStatusNoteActions({
    toggleEditor,
    currentNoteId,
    onStatusNoteDeleted,
    productionId,
    onNewStatusNoteClick
}: ProductionStatusNoteHeaderProps) {
    const { deleteNote, isNoteDeleting } = useDeleteNote();

    const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);

    const handleStopPropagation = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
    };

    const securedActions = useMemo<SecuredActionType[]>(
        () => [
            {
                securityAction: SECURITY_ACTIONS.READ,
                entityName: ENTITY_CONSTANTS.note,
                label: <I18n token="common.open.in.side.panel" />,
                icon: Icons.NewTabIcon,
                onClick: (event: MouseEvent<HTMLButtonElement>) => {
                    handleStopPropagation(event);
                    onNewStatusNoteClick?.({ isCreateNewNote: false });
                },
                dataTestId: 'icon-open-in-side-panel-status-note'
            },
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName: ENTITY_CONSTANTS.production,
                label: <I18n token="notes.new.note" />,
                icon: Icons.NoteAdd,
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    handleStopPropagation(e);
                    onNewStatusNoteClick?.({ isCreateNewNote: true });
                },
                dataTestId: 'icon-new-status-note'
            },
            {
                securityAction: SECURITY_ACTIONS.EDIT,
                entityName: ENTITY_CONSTANTS.note,
                label: <I18n token="dialog.edit" />,
                icon: Icons.Edit,
                onClick: (event: MouseEvent<HTMLButtonElement>) => {
                    handleStopPropagation(event);
                    toggleEditor(true);
                },
                dataTestId: 'icon-edit-status-note'
            },
            {
                securityAction: SECURITY_ACTIONS.DELETE,
                entityName: ENTITY_CONSTANTS.note,
                label: <I18n token="dialog.delete" />,
                icon: Icons.Delete,
                onClick: (event: MouseEvent<HTMLButtonElement>) => {
                    handleStopPropagation(event);
                    setDeleteNoteId(currentNoteId);
                },
                dataTestId: 'icon-delete-status-note'
            }
        ],
        [currentNoteId, onNewStatusNoteClick, toggleEditor]
    );

    const onConfirmDelete = (event: MouseEvent<HTMLButtonElement>) => {
        handleStopPropagation(event);
        if (deleteNoteId) {
            deleteNote({ noteId: deleteNoteId })
                .then((id) => {
                    id && onStatusNoteDeleted?.(deleteNoteId, productionId);
                })
                .finally(() => setDeleteNoteId(null));
        }
    };

    const onDeleteCancel = (event: MouseEvent<HTMLButtonElement>) => {
        handleStopPropagation(event);
        setDeleteNoteId(null);
    };

    return (
        <>
            <ResponsiveActions
                isActivated
                securedActions={securedActions}
                iconSx={{
                    color: 'inherit'
                }}
                containerSx={{ backgroundColor: 'inherit' }}
            />
            <Confirm
                open={Boolean(deleteNoteId)}
                title={<I18n token={'notes.delete'} />}
                body={<I18n token={'notes.delete.message'} />}
                onConfirm={onConfirmDelete}
                onCancel={onDeleteCancel}
                isLoading={isNoteDeleting}
            />
        </>
    );
}
